import { useState, useEffect } from 'react';
import { ArrowUpDown, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { CalculatorCard, NumberInput } from '../CalculatorCard';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export const IncomeExpenseCalculator = () => {
  const [income, setIncome] = useState(50000);
  const [housing, setHousing] = useState(15000);
  const [food, setFood] = useState(8000);
  const [transport, setTransport] = useState(4000);
  const [utilities, setUtilities] = useState(3000);
  const [entertainment, setEntertainment] = useState(3000);
  const [other, setOther] = useState(5000);
  
  const [balance, setBalance] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);

  useEffect(() => {
    const expenses = housing + food + transport + utilities + entertainment + other;
    setTotalExpenses(expenses);
    setBalance(income - expenses);
  }, [income, housing, food, transport, utilities, entertainment, other]);

  const expenseData = [
    { name: 'Bydlení', value: housing, color: '#22C55E' },
    { name: 'Jídlo', value: food, color: '#3B82F6' },
    { name: 'Doprava', value: transport, color: '#EAB308' },
    { name: 'Energie', value: utilities, color: '#A78BFA' },
    { name: 'Zábava', value: entertainment, color: '#F472B6' },
    { name: 'Ostatní', value: other, color: '#94A3B8' },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{data.name}</p>
          <p className="font-bold" style={{ color: data.color }}>{data.value.toLocaleString('cs-CZ')} Kč</p>
        </div>
      );
    }
    return null;
  };

  const isNegative = balance < 0;

  const result = (
    <div className="space-y-6">
      <div className={`p-4 rounded-xl ${isNegative ? 'bg-red-500/10 border border-red-500/30' : 'bg-green/10'}`}>
        <p className={`text-sm ${isNegative ? 'text-red-400' : 'text-gray-400'}`}>
          {isNegative ? 'Měsíční deficit' : 'Měsíční přebytek'}
        </p>
        <p className={`text-4xl font-bold ${isNegative ? 'text-red-500' : 'text-green'}`}>
          {isNegative ? '' : '+'}{balance.toLocaleString('cs-CZ')} Kč
        </p>
        {isNegative && (
          <div className="flex items-center gap-2 mt-2 text-red-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Utrácíte více než vyděláváte!</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-green mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs text-gray-400">Příjmy</span>
          </div>
          <p className="text-white font-bold text-lg">{income.toLocaleString('cs-CZ')} Kč</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-400 mb-1">
            <TrendingDown className="w-4 h-4" />
            <span className="text-xs text-gray-400">Výdaje</span>
          </div>
          <p className="text-white font-bold text-lg">{totalExpenses.toLocaleString('cs-CZ')} Kč</p>
        </div>
      </div>

      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={expenseData} layout="vertical">
            <XAxis 
              type="number"
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${value / 1000}k`}
            />
            <YAxis 
              type="category"
              dataKey="name"
              tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={55}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {expenseData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="prijmy-vydaje"
      title="Příjmy a Výdaje"
      subtitle="Sledujte svůj měsíční cashflow"
      icon={ArrowUpDown}
      tip={isNegative 
        ? "Máte záporný rozpočet! Projděte výdaje a najděte, kde škrtnout. Zrušte nepoužívaná předplatné a vyjednávejte lepší ceny služeb."
        : "Ideální míra úspor je 20%+. Každá zbylá koruna by měla mít účel: rezerva, investice, nebo splácení dluhů."}
      result={result}
      showResult={true}
      testId="income-expense-calculator"
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <NumberInput
            label="Čistý měsíční příjem"
            value={income}
            onChange={setIncome}
            suffix="Kč"
            testId="ie-income-input"
          />
        </div>
        <NumberInput
          label="Bydlení"
          value={housing}
          onChange={setHousing}
          suffix="Kč"
          testId="ie-housing-input"
        />
        <NumberInput
          label="Jídlo"
          value={food}
          onChange={setFood}
          suffix="Kč"
          testId="ie-food-input"
        />
        <NumberInput
          label="Doprava"
          value={transport}
          onChange={setTransport}
          suffix="Kč"
          testId="ie-transport-input"
        />
        <NumberInput
          label="Energie"
          value={utilities}
          onChange={setUtilities}
          suffix="Kč"
          testId="ie-utilities-input"
        />
        <NumberInput
          label="Zábava"
          value={entertainment}
          onChange={setEntertainment}
          suffix="Kč"
          testId="ie-entertainment-input"
        />
        <NumberInput
          label="Ostatní"
          value={other}
          onChange={setOther}
          suffix="Kč"
          testId="ie-other-input"
        />
      </div>
    </CalculatorCard>
  );
};
